import { Component, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, RefreshCw } from 'lucide-react'

function isChunkLoadError(error: Error): boolean {
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Loading CSS chunk')
  )
}

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class ErrorBoundaryInner extends Component<Props & { t: any }, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack)

    // Auto-reload on chunk loading failures (happens after deployments)
    if (isChunkLoadError(error)) {
      const reloadKey = 'immotron-chunk-reload'
      const lastReload = sessionStorage.getItem(reloadKey)
      if (!lastReload || Date.now() - Number(lastReload) > 10000) {
        sessionStorage.setItem(reloadKey, String(Date.now()))
        window.location.reload()
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      const { t } = this.props
      return (
        <div className="flex flex-col items-center justify-center min-h-[300px] p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('errors.unexpected', 'Unerwarteter Fehler')}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-md">
            {t('errors.unexpectedDescription', 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.')}
          </p>
          {this.state.error && (
            <pre className="text-xs text-gray-400 dark:text-gray-500 mb-4 max-w-lg overflow-auto bg-gray-50 dark:bg-gray-800 p-2 rounded">
              {this.state.error.message}
            </pre>
          )}
          <button
            data-testid="error-boundary-retry-btn"
            onClick={this.handleReset}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            {t('common.retry', 'Erneut versuchen')}
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

export default function ErrorBoundary({ children, fallback }: Props) {
  const { t } = useTranslation()
  return (
    <ErrorBoundaryInner t={t} fallback={fallback}>
      {children}
    </ErrorBoundaryInner>
  )
}
